// src\pages\admin\ReviewsManagement.jsx

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  FiStar,
  FiCheck,
  FiX,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiUser,
  FiMail,
  FiCalendar,
  FiEye,
  FiMessageSquare,
  FiTrendingUp,
  FiDownload,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronUp,
  FiBarChart2,
  FiPieChart,
  FiClock,
  FiFlag,
  FiAlertCircle,
  FiCheckCircle,
  FiSend,
  FiMaximize2,
  FiMinimize2,
  FiThumbsUp,
  FiThumbsDown,
  FiHeart,
  FiAward,
  FiBriefcase,
  FiCheck as FiCheckIcon,
  FiLoader,
  FiFileText,
  FiMoreVertical,
  FiExternalLink,
  FiCopy,
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// =============================================
// STAR RATING COMPONENT - Yellow Stars
// =============================================
const StarRatingDisplay = ({ rating, size = 'sm', showLabel = false }) => {
  const safeRating =
    typeof rating === 'number' ? rating : parseFloat(rating) || 0;
  const clampedRating = Math.min(Math.max(safeRating, 0), 5);

  const sizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6',
  };

  const starSize = sizes[size] || sizes.sm;

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <svg
            key={star}
            className={`${starSize} ${
              star <= clampedRating
                ? 'fill-current text-yellow-400'
                : 'fill-current text-gray-300 dark:text-gray-600'
            }`}
            viewBox="0 0 2000 2000"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1940.9,893.45c-13,44.44-40.18,82.71-78.67,110.68l-326.25,237a10.56,10.56,0,0,0-3.83,11.8l124.62,383.52c21.12,65,10.52,133.53-29.07,188-40.08,55.16-104.21,88.09-171.56,88.09-44.05,0-86.71-14.08-123.37-40.72l-326.24-237a10.57,10.57,0,0,0-12.4,0l-326.24,237c-36.66,26.64-79.32,40.72-123.37,40.72-67.36,0-131.49-32.93-171.56-88.08-39.59-54.5-50.19-123-29.07-188l124.62-383.53a10.53,10.53,0,0,0-3.83-11.78l-326.25-237c-38.49-28-65.69-66.24-78.67-110.68a213.4,213.4,0,0,1,73.73-226.91c36.62-28.32,81.12-43.29,128.7-43.29H665.45a10.53,10.53,0,0,0,10-7.29L800.09,232.44c14.7-45.25,42.69-82.95,80.95-109a213.38,213.38,0,0,1,238.58,0c38.26,26.07,66.25,63.77,81,109L1325.18,616a10.53,10.53,0,0,0,10,7.29h403.26c47.58,0,92.08,15,128.7,43.29a213.4,213.4,0,0,1,73.73,226.91Z" />
          </svg>
        ))}
      </div>
      {showLabel && (
        <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>
          {clampedRating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

// =============================================
// STATUS BADGE - Black & White
// =============================================
const StatusBadge = ({ status }) => {
  const configs = {
    approved: {
      label: 'Approved',
      className:
        'bg-black/10 dark:bg-white/10 text-black dark:text-white border-black/20 dark:border-white/20',
    },
    pending: {
      label: 'Pending',
      className:
        'bg-black/5 dark:bg-white/5 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600',
    },
    rejected: {
      label: 'Rejected',
      className:
        'bg-black/5 dark:bg-white/5 text-red-600 dark:text-red-400 border-red-300 dark:border-red-800',
    },
  };

  const config = configs[status] || configs.pending;

  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${config.className}`}
    >
      {config.label}
    </span>
  );
};

// =============================================
// REVIEW CARD - Glassmorphic with Gradient Hover
// =============================================
const GlassReviewCard = ({
  review,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onStatusUpdate,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const date = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

  return (
    <div
      className={`group relative rounded-2xl overflow-hidden transition-all duration-500 ${
        isSelected ? 'ring-2 ring-black dark:ring-white' : ''
      } ${isHovered ? 'scale-[1.02] shadow-2xl' : 'shadow-sm'}`}
      style={{
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glassmorphic Background Effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 50%, rgba(0,0,0,0.05) 100%)',
          }}
        />
        {/* Gradient Shimmer on Hover */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            background:
              'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.08) 50%, transparent 60%)',
            backgroundSize: '200% 100%',
            animation: isHovered ? 'shimmer 1.5s infinite' : 'none',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(review.id)}
              className="w-4 h-4 rounded cursor-pointer flex-shrink-0"
              style={{ accentColor: 'var(--text)' }}
            />
            {review.imageUrl ? (
              <img
                src={review.imageUrl}
                alt={review.name}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-black/10 dark:ring-white/10 flex-shrink-0"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                style={{
                  background:
                    'linear-gradient(135deg, var(--text) 0%, var(--muted) 100%)',
                }}
              >
                <span className="text-white font-bold text-sm">
                  {review.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="min-w-0">
              <h3
                className="font-semibold text-sm truncate"
                style={{ color: 'var(--text)' }}
              >
                {review.name}
              </h3>
              {review.occupation && (
                <p
                  className="text-xs truncate flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiBriefcase className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{review.occupation}</span>
                </p>
              )}
            </div>
          </div>
          <StatusBadge status={review.status} />
        </div>

        {/* Rating - Yellow Stars */}
        <div className="flex items-center gap-2 mb-3">
          <StarRatingDisplay rating={review.rating} size="sm" />
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
          >
            {review.rating}.0
          </span>
        </div>

        {/* Service Type */}
        {review.serviceType && (
          <div className="mb-2">
            <span
              className="text-xs px-2 py-1 rounded-lg inline-flex items-center gap-1 transition-all duration-300 group-hover:bg-black/10 dark:group-hover:bg-white/10"
              style={{
                backgroundColor: 'var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiFlag className="w-3 h-3" />
              {review.serviceType}
            </span>
          </div>
        )}

        {/* Message */}
        <p
          className="text-sm leading-relaxed line-clamp-3 mb-3"
          style={{ color: 'var(--muted)' }}
        >
          {review.message}
        </p>

        {/* Footer */}
        <div
          className="flex items-center justify-between pt-3 border-t transition-all duration-300"
          style={{ borderColor: 'var(--border)' }}
        >
          <div
            className="flex items-center gap-2 text-xs"
            style={{ color: 'var(--muted)' }}
          >
            <FiCalendar className="w-3 h-3 flex-shrink-0" />
            {date}
            {review.imagePublicId && (
              <span className="flex items-center gap-1 opacity-60">
                <FiFileText className="w-3 h-3" />
                Image
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-0.5">
            {review.status !== 'approved' && (
              <button
                onClick={() => onStatusUpdate(review.id, 'approved')}
                className="p-1.5 rounded-lg transition-all duration-300 hover:scale-110 hover:bg-black/10 dark:hover:bg-white/10"
                style={{ color: 'var(--text)' }}
                title="Approve"
              >
                <FiCheck className="w-4 h-4" />
              </button>
            )}
            {review.status !== 'rejected' && (
              <button
                onClick={() => onStatusUpdate(review.id, 'rejected')}
                className="p-1.5 rounded-lg transition-all duration-300 hover:scale-110 hover:bg-black/10 dark:hover:bg-white/10"
                style={{ color: 'var(--text)' }}
                title="Reject"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onEdit(review)}
              className="p-1.5 rounded-lg transition-all duration-300 hover:scale-110 hover:bg-black/10 dark:hover:bg-white/10"
              style={{ color: 'var(--text)' }}
              title="Edit"
            >
              <FiEdit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(review.id)}
              className="p-1.5 rounded-lg transition-all duration-300 hover:scale-110 hover:bg-red-500/10"
              style={{ color: 'var(--text)' }}
              title="Delete"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================
// LIST VIEW ITEM
// =============================================
const ReviewListItem = ({
  review,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onStatusUpdate,
}) => {
  const date = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString()
    : '';

  return (
    <div
      className={`p-4 rounded-xl transition-all duration-300 hover:shadow-md ${
        isSelected ? 'ring-2 ring-black dark:ring-white' : ''
      }`}
      style={{
        backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)',
      }}
    >
      <div className="flex items-start gap-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(review.id)}
          className="mt-2 w-4 h-4 rounded cursor-pointer flex-shrink-0"
          style={{ accentColor: 'var(--text)' }}
        />
        {review.imageUrl ? (
          <img
            src={review.imageUrl}
            alt={review.name}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{
              background:
                'linear-gradient(135deg, var(--text) 0%, var(--muted) 100%)',
            }}
          >
            <span className="text-white font-bold text-sm">
              {review.name?.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <h3
                className="font-semibold truncate"
                style={{ color: 'var(--text)' }}
              >
                {review.name}
              </h3>
              {review.occupation && (
                <span
                  className="text-xs truncate"
                  style={{ color: 'var(--muted)' }}
                >
                  • {review.occupation}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <StarRatingDisplay rating={review.rating} size="sm" />
              <StatusBadge status={review.status} />
            </div>
          </div>
          {review.serviceType && (
            <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
              <span className="font-medium">Service:</span> {review.serviceType}
            </p>
          )}
          <p className="text-sm line-clamp-2" style={{ color: 'var(--muted)' }}>
            {review.message}
          </p>
          <div
            className="flex items-center gap-3 mt-2 text-xs"
            style={{ color: 'var(--muted)' }}
          >
            <span className="flex items-center gap-1">
              <FiCalendar className="w-3 h-3" />
              {date}
            </span>
            {review.imagePublicId && (
              <span className="flex items-center gap-1">
                <FiFileText className="w-3 h-3" />
                Image
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {review.status !== 'approved' && (
            <button
              onClick={() => onStatusUpdate(review.id, 'approved')}
              className="p-1.5 rounded-lg transition-all hover:scale-110 hover:bg-black/10 dark:hover:bg-white/10"
              style={{ color: 'var(--text)' }}
              title="Approve"
            >
              <FiCheck className="w-4 h-4" />
            </button>
          )}
          {review.status !== 'rejected' && (
            <button
              onClick={() => onStatusUpdate(review.id, 'rejected')}
              className="p-1.5 rounded-lg transition-all hover:scale-110 hover:bg-black/10 dark:hover:bg-white/10"
              style={{ color: 'var(--text)' }}
              title="Reject"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onEdit(review)}
            className="p-1.5 rounded-lg transition-all hover:scale-110 hover:bg-black/10 dark:hover:bg-white/10"
            style={{ color: 'var(--text)' }}
            title="Edit"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(review.id)}
            className="p-1.5 rounded-lg transition-all hover:scale-110 hover:bg-red-500/10"
            style={{ color: 'var(--text)' }}
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================
// MAIN COMPONENT
// =============================================
function ReviewsManagement() {
  const { getIdToken } = useAuth();
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  // State
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({
    current: 0,
    total: 0,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteDetails, setDeleteDetails] = useState(null);
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  });
  const [timelineData, setTimelineData] = useState([]);
  const [timelineRange, setTimelineRange] = useState('30days');
  const [editForm, setEditForm] = useState({
    name: '',
    occupation: '',
    serviceType: '',
    rating: 5,
    message: '',
    status: 'pending',
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('success');

  // Chart colors - Black & White theme with Yellow stars
  const chartColors = useMemo(
    () => ({
      primary: isDark ? '#ffffff' : '#000000',
      primaryLight: isDark ? '#888888' : '#444444',
      secondary: isDark ? '#666666' : '#999999',
      star: '#FBBF24', // Yellow for stars
      starDark: '#F59E0B',
      success: isDark ? '#4ade80' : '#22c55e',
      warning: isDark ? '#fbbf24' : '#eab308',
      error: isDark ? '#f87171' : '#ef4444',
      text: isDark ? '#f1f5f9' : '#1e293b',
      textSecondary: isDark ? '#94a3b8' : '#64748b',
      grid: isDark ? '#334155' : '#e2e8f0',
      background: isDark ? '#0f172a' : '#ffffff',
      tooltipBg: isDark ? '#1e293b' : '#ffffff',
      tooltipBorder: isDark ? '#334155' : '#e5e7eb',
    }),
    [isDark],
  );

  // =============================================
  // TOAST NOTIFICATION
  // =============================================
  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // =============================================
  // GENERATE TIMELINE DATA - FIXED
  // =============================================
  const generateTimelineData = (reviewsList, range) => {
    try {
      const days = range === '30days' ? 30 : range === '90days' ? 90 : 365;
      const timeline = [];
      const now = new Date();

      for (let i = days; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];

        // FIX: Safely filter reviews with valid dates
        const count = reviewsList.filter(r => {
          try {
            // Check if createdAt exists and is valid
            if (!r.createdAt) return false;
            const reviewDate = new Date(r.createdAt);
            // Check if date is valid
            if (isNaN(reviewDate.getTime())) return false;
            return (
              reviewDate.toISOString().split('T')[0] === dateStr &&
              r.status === 'approved'
            );
          } catch (err) {
            return false;
          }
        }).length;

        timeline.push({
          date: dateStr,
          count,
          name: date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          }),
        });
      }
      setTimelineData(timeline);
    } catch (error) {
      console.error('Error generating timeline data:', error);
      // Set empty timeline data on error
      setTimelineData([]);
    }
  };

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      const response = await fetch('/api/reviews?action=admin-list&limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        // FIX: Ensure all reviews have valid date objects
        const reviewsWithValidDates = (data.reviews || []).map(review => ({
          ...review,
          // If createdAt is invalid, set to current date
          createdAt:
            review.createdAt && !isNaN(new Date(review.createdAt).getTime())
              ? review.createdAt
              : new Date().toISOString(),
        }));
        setReviews(reviewsWithValidDates);
        setStats(
          data.stats || {
            totalReviews: 0,
            averageRating: 0,
            ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
          },
        );
        generateTimelineData(reviewsWithValidDates, timelineRange);
      } else {
        showToast(data.message || 'Failed to fetch reviews', 'error');
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showToast('Error fetching reviews', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getIdToken, timelineRange]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // =============================================
  // FILTER AND SORT REVIEWS
  // =============================================
  useEffect(() => {
    let filtered = [...reviews];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    if (ratingFilter !== 'all') {
      filtered = filtered.filter(r => r.rating === parseInt(ratingFilter));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        r =>
          r.name?.toLowerCase().includes(term) ||
          r.occupation?.toLowerCase().includes(term) ||
          r.serviceType?.toLowerCase().includes(term) ||
          r.message?.toLowerCase().includes(term),
      );
    }

    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'date':
          aVal = new Date(a.createdAt || 0);
          bVal = new Date(b.createdAt || 0);
          break;
        case 'rating':
          aVal = a.rating || 0;
          bVal = b.rating || 0;
          break;
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        default:
          aVal = new Date(a.createdAt || 0);
          bVal = new Date(b.createdAt || 0);
      }
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

    setFilteredReviews(filtered);
  }, [reviews, statusFilter, ratingFilter, searchTerm, sortBy, sortOrder]);

  // =============================================
  // SELECTION HANDLERS
  // =============================================
  const toggleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredReviews.map(r => r.id));
    }
    setSelectAll(!selectAll);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  // =============================================
  // DELETE HANDLERS
  // =============================================
  const getDeleteDetails = ids => {
    let totalWithImages = 0;
    let totalWithNotifications = 0;
    const reviewsToDelete = reviews.filter(r => ids.includes(r.id));

    reviewsToDelete.forEach(r => {
      if (r.imagePublicId) totalWithImages++;
      if (r.notificationId) totalWithNotifications++;
    });

    return {
      count: ids.length,
      withImages: totalWithImages,
      withNotifications: totalWithNotifications,
      reviews: reviewsToDelete,
    };
  };

  const handleDelete = async id => {
    const review = reviews.find(r => r.id === id);
    if (!review) return;

    const details = {
      count: 1,
      withImages: review.imagePublicId ? 1 : 0,
      withNotifications: review.notificationId ? 1 : 0,
      reviews: [review],
    };

    setDeleteDetails(details);
    setShowDeleteConfirm(true);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      showToast('No reviews selected', 'warning');
      return;
    }
    const details = getDeleteDetails(selectedIds);
    setDeleteDetails(details);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteDetails) return;

    setIsDeleting(true);
    setDeleteProgress({ current: 0, total: deleteDetails.count });

    let deletedCount = 0;
    let failedCount = 0;

    for (const review of deleteDetails.reviews) {
      try {
        const token = await getIdToken();
        const response = await fetch(
          `/api/reviews?action=delete&id=${review.id}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await response.json();

        if (data.success) {
          deletedCount++;
        } else {
          failedCount++;
        }
      } catch (err) {
        failedCount++;
      }

      setDeleteProgress({
        current: deletedCount + failedCount,
        total: deleteDetails.count,
      });
    }

    setShowDeleteConfirm(false);
    setIsDeleting(false);

    if (deletedCount > 0) {
      showToast(`✅ ${deletedCount} review(s) deleted successfully`, 'success');
    }
    if (failedCount > 0) {
      showToast(`⚠️ ${failedCount} review(s) failed to delete`, 'warning');
    }

    clearSelection();
    setDeleteDetails(null);
    fetchReviews();
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteDetails(null);
    setIsDeleting(false);
  };

  // =============================================
  // STATUS UPDATE HANDLERS
  // =============================================
  const handleBulkStatusUpdate = async status => {
    if (selectedIds.length === 0) {
      showToast('No reviews selected', 'warning');
      return;
    }

    try {
      let successCount = 0;
      for (const id of selectedIds) {
        const result = await handleUpdateStatus(id, status);
        if (result) successCount++;
      }
      clearSelection();
      fetchReviews();
      showToast(
        `✅ ${successCount} review(s) ${status}ed successfully`,
        'success',
      );
    } catch (error) {
      console.error(`Error bulk ${status}ing reviews:`, error);
      showToast(`Failed to ${status} selected reviews`, 'error');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const token = await getIdToken();
      const response = await fetch('/api/reviews?action=update-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, status }),
      });
      const data = await response.json();
      if (data.success) {
        fetchReviews();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating status:', error);
      return false;
    }
  };

  // =============================================
  // EDIT HANDLERS
  // =============================================
  const handleEdit = review => {
    setSelectedReview(review);
    setEditForm({
      name: review.name || '',
      occupation: review.occupation || '',
      serviceType: review.serviceType || '',
      rating: review.rating || 5,
      message: review.message || '',
      status: review.status || 'pending',
    });
    setImagePreview(review.imageUrl || null);
    setShowEditModal(true);
  };

  const handleUpdate = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = await getIdToken();
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('occupation', editForm.occupation);
      formData.append('serviceType', editForm.serviceType);
      formData.append('rating', editForm.rating);
      formData.append('message', editForm.message);
      formData.append('status', editForm.status);
      if (imageFile) formData.append('image', imageFile);

      const response = await fetch(
        `/api/reviews?action=update&id=${selectedReview.id}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );
      const data = await response.json();
      if (data.success) {
        fetchReviews();
        setShowEditModal(false);
        setImageFile(null);
        setImagePreview(null);
        showToast('✅ Review updated successfully', 'success');
      } else {
        showToast(data.message || 'Failed to update review', 'error');
      }
    } catch (error) {
      console.error('Error updating review:', error);
      showToast('Error updating review', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // =============================================
  // EXPORT HANDLER
  // =============================================
  const handleExport = () => {
    const csvData = filteredReviews.map(r => ({
      Name: r.name || '',
      Occupation: r.occupation || '',
      'Service Type': r.serviceType || '',
      Rating: r.rating || 0,
      Message: r.message || '',
      Status: r.status || '',
      'Has Image': r.imageUrl ? 'Yes' : 'No',
      Date: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
    }));

    if (csvData.length === 0) {
      showToast('No data to export', 'warning');
      return;
    }

    const headers = Object.keys(csvData[0]);
    const csv = [
      headers.join(','),
      ...csvData.map(row => headers.map(h => `"${row[h] || ''}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reviews-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('✅ Export completed successfully', 'success');
  };

  // =============================================
  // RATING DATA
  // =============================================
  const ratingsData = [
    { stars: 5, count: stats.ratingDistribution?.[5] || 0 },
    { stars: 4, count: stats.ratingDistribution?.[4] || 0 },
    { stars: 3, count: stats.ratingDistribution?.[3] || 0 },
    { stars: 2, count: stats.ratingDistribution?.[2] || 0 },
    { stars: 1, count: stats.ratingDistribution?.[1] || 0 },
  ];

  // Filter out ratings with zero count for the distribution bars
  const filteredRatingsData = ratingsData.filter(r => r.count > 0);

  const statusChartData = [
    {
      name: 'Approved',
      value: reviews.filter(r => r.status === 'approved').length,
    },
    {
      name: 'Pending',
      value: reviews.filter(r => r.status === 'pending').length,
    },
    {
      name: 'Rejected',
      value: reviews.filter(r => r.status === 'rejected').length,
    },
  ].filter(item => item.value > 0);

  const ratingChartData = ratingsData.map(r => ({
    rating: `${r.stars} Star${r.stars > 1 ? 's' : ''}`,
    count: r.count,
  }));

  // =============================================
  // DELETE CONFIRMATION MODAL
  // =============================================
  const DeleteConfirmModal = () => {
    if (!deleteDetails) return null;

    return (
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
      >
        <div
          className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden"
          style={{
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
          }}
        >
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-black/10 dark:bg-white/10">
                <FiTrash2
                  className="w-6 h-6"
                  style={{ color: 'var(--text)' }}
                />
              </div>
              <div>
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  Delete Review{deleteDetails.count > 1 ? 's' : ''}
                </h3>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  This action cannot be undone
                </p>
              </div>
            </div>

            <div
              className="p-4 rounded-xl mb-4"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p
                className="text-sm font-medium mb-2"
                style={{ color: 'var(--text)' }}
              >
                The following will be permanently removed:
              </p>
              <ul
                className="space-y-1 text-sm"
                style={{ color: 'var(--muted)' }}
              >
                <li className="flex items-center gap-2">
                  <FiAlertCircle className="w-4 h-4" />
                  {deleteDetails.count} review
                  {deleteDetails.count > 1 ? 's' : ''}
                </li>
                {deleteDetails.withImages > 0 && (
                  <li className="flex items-center gap-2">
                    <FiFileText className="w-4 h-4" />
                    {deleteDetails.withImages} profile image
                    {deleteDetails.withImages > 1 ? 's' : ''}
                  </li>
                )}
                {deleteDetails.withNotifications > 0 && (
                  <li className="flex items-center gap-2">
                    <FiMessageSquare className="w-4 h-4" />
                    {deleteDetails.withNotifications} notification
                    {deleteDetails.withNotifications > 1 ? 's' : ''}
                  </li>
                )}
              </ul>
            </div>

            {isDeleting && (
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span style={{ color: 'var(--text)' }}>Deleting...</span>
                  <span style={{ color: 'var(--muted)' }}>
                    {deleteProgress.current} / {deleteProgress.total}
                  </span>
                </div>
                <div
                  className="h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${(deleteProgress.current / deleteProgress.total) * 100}%`,
                      backgroundColor: 'var(--text)',
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                style={{
                  backgroundColor: 'var(--text)',
                  color: 'var(--bg)',
                }}
              >
                {isDeleting ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FiTrash2 className="w-4 h-4" />
                    Delete {deleteDetails.count > 1 ? 'All' : 'Review'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // =============================================
  // TOAST COMPONENT
  // =============================================
  const Toast = () => {
    if (!toastMessage) return null;

    return (
      <div
        className="fixed top-4 right-4 z-[99999] px-4 py-3 rounded-xl shadow-2xl animate-slide-in-right max-w-sm"
        style={{
          backgroundColor: 'var(--bg)',
          border: `1px solid ${toastType === 'error' ? '#ef4444' : toastType === 'warning' ? '#eab308' : 'var(--border)'}`,
        }}
      >
        <p className="text-sm" style={{ color: 'var(--text)' }}>
          {toastMessage}
        </p>
      </div>
    );
  };

  // =============================================
  // CSS ANIMATIONS
  // =============================================
  const styles = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes slide-in-right {
      0% { transform: translateX(100px); opacity: 0; }
      100% { transform: translateX(0); opacity: 1; }
    }
    .animate-slide-in-right {
      animation: slide-in-right 0.3s ease-out forwards;
    }
  `;

  // =============================================
  // RENDER
  // =============================================
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <style>{styles}</style>
      <Toast />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Reviews Management
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            Manage and moderate client reviews
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedIds.length > 0 && (
            <>
              <button
                onClick={() => handleBulkStatusUpdate('approved')}
                className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80 text-sm"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiCheck className="w-4 h-4" />
                Approve
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('rejected')}
                className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80 text-sm"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiX className="w-4 h-4" />
                Reject
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80 text-sm"
                style={{
                  backgroundColor: 'var(--text)',
                  color: 'var(--bg)',
                }}
              >
                <FiTrash2 className="w-4 h-4" />
                Delete ({selectedIds.length})
              </button>
            </>
          )}
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm ${
              showAnalytics ? 'bg-black/10 dark:bg-white/10' : ''
            }`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiTrendingUp className="w-4 h-4" />
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80 text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiDownload className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => {
              setRefreshing(true);
              fetchReviews();
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all hover:opacity-80 text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Rating Distribution */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="font-semibold flex items-center gap-2 mb-4"
              style={{ color: 'var(--text)' }}
            >
              <FiBarChart2 className="w-4 h-4" />
              Rating Distribution
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={ratingChartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartColors.grid}
                  />
                  <XAxis
                    dataKey="rating"
                    tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: chartColors.tooltipBg,
                      borderColor: chartColors.tooltipBorder,
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: chartColors.text }}
                    itemStyle={{ color: chartColors.text }}
                  />
                  <Bar
                    dataKey="count"
                    fill={chartColors.primary}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <h3
              className="font-semibold flex items-center gap-2 mb-4"
              style={{ color: 'var(--text)' }}
            >
              <FiPieChart className="w-4 h-4" />
              Status Distribution
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => {
                      const colors = ['#000000', '#666666', '#999999'];
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={isDark ? '#ffffff' : colors[index]}
                          opacity={isDark ? 0.8 : 1}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: chartColors.tooltipBg,
                      borderColor: chartColors.tooltipBorder,
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: chartColors.text }}
                    itemStyle={{ color: chartColors.text }}
                  />
                  <Legend
                    formatter={value => (
                      <span
                        style={{
                          color: chartColors.textSecondary,
                          fontSize: '11px',
                        }}
                      >
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Timeline Chart */}
          <div
            className="lg:col-span-2 p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3
                className="font-semibold flex items-center gap-2"
                style={{ color: 'var(--text)' }}
              >
                <FiCalendar className="w-4 h-4" />
                Review Timeline
              </h3>
              <div className="flex gap-1">
                {['30days', '90days', 'lifetime'].map(range => (
                  <button
                    key={range}
                    onClick={() => setTimelineRange(range)}
                    className={`px-3 py-1 rounded-lg text-xs transition-all ${
                      timelineRange === range
                        ? 'bg-black/20 dark:bg-white/20 font-medium'
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                    style={{ color: 'var(--text)' }}
                  >
                    {range === '30days'
                      ? 'Last 30 Days'
                      : range === '90days'
                        ? 'Last 90 Days'
                        : 'Lifetime'}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={timelineData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={chartColors.primary}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.primary}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartColors.grid}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    interval={
                      timelineRange === '30days'
                        ? 5
                        : timelineRange === '90days'
                          ? 14
                          : 30
                    }
                  />
                  <YAxis
                    tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: chartColors.tooltipBg,
                      borderColor: chartColors.tooltipBorder,
                      borderRadius: '8px',
                    }}
                    labelStyle={{ color: chartColors.text }}
                    itemStyle={{ color: chartColors.text }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={chartColors.primary}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCount)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards - Yellow Stars, Black & White everything else */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div
          className="p-4 rounded-xl text-center transition-all hover:scale-105"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            {stats.totalReviews || 0}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Total Reviews
          </div>
        </div>
        <div
          className="p-4 rounded-xl text-center transition-all hover:scale-105"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="flex justify-center mb-1">
            <StarRatingDisplay rating={stats.averageRating || 0} size="md" />
          </div>
          <div className="text-xl font-bold" style={{ color: 'var(--text)' }}>
            {stats.averageRating || 0}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Avg Rating
          </div>
        </div>
        <div
          className="p-4 rounded-xl text-center transition-all hover:scale-105"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {reviews.filter(r => r.status === 'approved').length}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Approved
          </div>
        </div>
        <div
          className="p-4 rounded-xl text-center transition-all hover:scale-105"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {reviews.filter(r => r.status === 'pending').length}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Pending
          </div>
        </div>
        <div
          className="p-4 rounded-xl text-center transition-all hover:scale-105"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
            {reviews.filter(r => r.status === 'rejected').length}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Rejected
          </div>
        </div>
        <div
          className="p-4 rounded-xl text-center transition-all hover:scale-105"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            {stats.totalReviews > 0
              ? (
                  (reviews.filter(r => r.rating >= 4 && r.status === 'approved')
                    .length /
                    stats.totalReviews) *
                  100
                ).toFixed(0)
              : 0}
            %
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Satisfaction
          </div>
        </div>
      </div>

      {/* Rating Breakdown - Only show bars with values > 0 */}
      <div
        className="p-5 rounded-2xl border"
        style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
      >
        <h3
          className="font-semibold mb-4 flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <FiStar className="w-4 h-4 text-yellow-400" />
          Rating Breakdown
        </h3>
        <div className="space-y-3">
          {ratingsData.map(rating => {
            const hasValue = rating.count > 0;
            const percentage =
              stats.totalReviews > 0
                ? (rating.count / stats.totalReviews) * 100
                : 0;

            return (
              <div key={rating.stars} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-20 flex-shrink-0">
                  <span className="text-sm" style={{ color: 'var(--text)' }}>
                    {rating.stars}
                  </span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-3 h-3 ${
                          i < rating.stars
                            ? 'fill-current text-yellow-400'
                            : 'fill-current text-gray-300 dark:text-gray-600'
                        }`}
                        viewBox="0 0 2000 2000"
                      >
                        <path d="M1940.9,893.45c-13,44.44-40.18,82.71-78.67,110.68l-326.25,237a10.56,10.56,0,0,0-3.83,11.8l124.62,383.52c21.12,65,10.52,133.53-29.07,188-40.08,55.16-104.21,88.09-171.56,88.09-44.05,0-86.71-14.08-123.37-40.72l-326.24-237a10.57,10.57,0,0,0-12.4,0l-326.24,237c-36.66,26.64-79.32,40.72-123.37,40.72-67.36,0-131.49-32.93-171.56-88.08-39.59-54.5-50.19-123-29.07-188l124.62-383.53a10.53,10.53,0,0,0-3.83-11.78l-326.25-237c-38.49-28-65.69-66.24-78.67-110.68a213.4,213.4,0,0,1,73.73-226.91c36.62-28.32,81.12-43.29,128.7-43.29H665.45a10.53,10.53,0,0,0,10-7.29L800.09,232.44c14.7-45.25,42.69-82.95,80.95-109a213.38,213.38,0,0,1,238.58,0c38.26,26.07,66.25,63.77,81,109L1325.18,616a10.53,10.53,0,0,0,10,7.29h403.26c47.58,0,92.08,15,128.7,43.29a213.4,213.4,0,0,1,73.73,226.91Z" />
                      </svg>
                    ))}
                  </div>
                </div>
                {/* Only show bar if count > 0 */}
                {hasValue ? (
                  <div
                    className="flex-1 h-2.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(percentage, 0.5)}%`,
                        backgroundColor: 'var(--text)',
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className="flex-1 h-2.5 rounded-full"
                    style={{ backgroundColor: 'var(--border)' }}
                  />
                )}
                <div
                  className="text-sm w-12 text-right font-mono"
                  style={{ color: 'var(--muted)' }}
                >
                  {rating.count}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
              statusFilter === 'all'
                ? 'bg-black/20 dark:bg-white/20 font-medium'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
              statusFilter === 'approved'
                ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)' }}
          >
            Approved
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
              statusFilter === 'pending'
                ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)' }}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
              statusFilter === 'rejected'
                ? 'bg-red-500/20 text-red-600 dark:text-red-400'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)' }}
          >
            Rejected
          </button>
        </div>
        <div className="flex gap-2">
          <select
            value={ratingFilter}
            onChange={e => setRatingFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm outline-none cursor-pointer"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm outline-none cursor-pointer"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            <option value="date">Sort by Date</option>
            <option value="rating">Sort by Rating</option>
            <option value="name">Sort by Name</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-1.5 rounded-lg transition-all"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            {sortOrder === 'asc' ? (
              <FiChevronUp className="w-4 h-4" />
            ) : (
              <FiChevronDown className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-lg transition-all ${
              viewMode === 'grid' ? 'bg-black/10 dark:bg-white/10' : ''
            }`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-lg transition-all ${
              viewMode === 'list' ? 'bg-black/10 dark:bg-white/10' : ''
            }`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiList className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <FiSearch
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
          style={{ color: 'var(--muted)' }}
        />
        <input
          type="text"
          placeholder="Search by name, occupation, service type, or message..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl outline-none transition-all"
          style={{
            backgroundColor: 'transparent',
            border: '1px solid var(--border)',
            color: 'var(--text)',
          }}
        />
      </div>

      {/* Select All */}
      {filteredReviews.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm transition-all hover:opacity-80"
            style={{ color: 'var(--muted)' }}
          >
            <span
              className="w-4 h-4 rounded border-2 flex items-center justify-center"
              style={{ borderColor: 'var(--border)' }}
            >
              {selectAll && (
                <FiCheckIcon
                  className="w-3 h-3"
                  style={{ color: 'var(--text)' }}
                />
              )}
            </span>
            {selectAll ? 'Deselect All' : 'Select All'}
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              ({filteredReviews.length} items)
            </span>
          </button>
          {selectedIds.length > 0 && (
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              • {selectedIds.length} selected
            </span>
          )}
        </div>
      )}

      {/* Reviews Display */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div
            className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--text)' }}
          />
        </div>
      ) : filteredReviews.length === 0 ? (
        <div
          className="text-center py-12 rounded-2xl border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <FiStar
            className="w-12 h-12 mx-auto mb-3 opacity-50"
            style={{ color: 'var(--muted)' }}
          />
          <p style={{ color: 'var(--muted)' }}>No reviews found</p>
          <button
            onClick={() => {
              setStatusFilter('all');
              setRatingFilter('all');
              setSearchTerm('');
            }}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Clear Filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredReviews.map(review => (
            <GlassReviewCard
              key={review.id}
              review={review}
              isSelected={selectedIds.includes(review.id)}
              onSelect={toggleSelect}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onStatusUpdate={handleUpdateStatus}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredReviews.map(review => (
            <ReviewListItem
              key={review.id}
              review={review}
              isSelected={selectedIds.includes(review.id)}
              onSelect={toggleSelect}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onStatusUpdate={handleUpdateStatus}
            />
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <div
            className="relative max-w-lg w-full rounded-2xl shadow-2xl overflow-hidden"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  Edit Review
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1 rounded-full hover:bg-black/5"
                >
                  <FiX className="w-5 h-5" style={{ color: 'var(--muted)' }} />
                </button>
              </div>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={e =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none focus:ring-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    required
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Occupation
                  </label>
                  <input
                    type="text"
                    value={editForm.occupation}
                    onChange={e =>
                      setEditForm({ ...editForm, occupation: e.target.value })
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Service Type
                  </label>
                  <input
                    type="text"
                    value={editForm.serviceType}
                    onChange={e =>
                      setEditForm({ ...editForm, serviceType: e.target.value })
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Rating
                  </label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() =>
                          setEditForm({ ...editForm, rating: star })
                        }
                        className="transition-transform hover:scale-110"
                      >
                        <svg
                          className={`w-8 h-8 ${
                            star <= editForm.rating
                              ? 'fill-current text-yellow-400'
                              : 'fill-current text-gray-300 dark:text-gray-600'
                          }`}
                          viewBox="0 0 2000 2000"
                        >
                          <path d="M1940.9,893.45c-13,44.44-40.18,82.71-78.67,110.68l-326.25,237a10.56,10.56,0,0,0-3.83,11.8l124.62,383.52c21.12,65,10.52,133.53-29.07,188-40.08,55.16-104.21,88.09-171.56,88.09-44.05,0-86.71-14.08-123.37-40.72l-326.24-237a10.57,10.57,0,0,0-12.4,0l-326.24,237c-36.66,26.64-79.32,40.72-123.37,40.72-67.36,0-131.49-32.93-171.56-88.08-39.59-54.5-50.19-123-29.07-188l124.62-383.53a10.53,10.53,0,0,0-3.83-11.78l-326.25-237c-38.49-28-65.69-66.24-78.67-110.68a213.4,213.4,0,0,1,73.73-226.91c36.62-28.32,81.12-43.29,128.7-43.29H665.45a10.53,10.53,0,0,0,10-7.29L800.09,232.44c14.7-45.25,42.69-82.95,80.95-109a213.38,213.38,0,0,1,238.58,0c38.26,26.07,66.25,63.77,81,109L1325.18,616a10.53,10.53,0,0,0,10,7.29h403.26c47.58,0,92.08,15,128.7,43.29a213.4,213.4,0,0,1,73.73,226.91Z" />
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Review Message
                  </label>
                  <textarea
                    value={editForm.message}
                    onChange={e =>
                      setEditForm({ ...editForm, message: e.target.value })
                    }
                    rows="4"
                    className="w-full px-4 py-2 rounded-xl outline-none resize-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    required
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Status
                  </label>
                  <select
                    value={editForm.status}
                    onChange={e =>
                      setEditForm({ ...editForm, status: e.target.value })
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Profile Image
                  </label>
                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-16 h-16 rounded-full object-cover mb-2"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        setImageFile(file);
                        setImagePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="w-full text-sm"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-2 rounded-xl"
                    style={{
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    {submitting ? (
                      <>
                        <FiLoader className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal />
    </div>
  );
}

export default ReviewsManagement;
